/**
 * lib/inviteToken.ts — the two short-lived stashes the org flows need, and
 * their disposal.
 *
 * ── WHY sessionStorage AND NOT THE PERSISTED APP STATE ────────────────────
 * `aska-platform-state` (lib/persistedState.ts) is localStorage and is NOT
 * uid-scoped: it survives into the next person to sign in on a shared org
 * workstation. Both values here name a human — an invited colleague's email,
 * and a token that grants membership of a company — so neither may live there.
 * sessionStorage dies with the tab, both keys are org-scoped, both carry a
 * 30-minute TTL checked on read, and `clearOrgStashes()` is called on sign-out
 * so a second person on the same tab inherits nothing.
 *
 * ── AND WHY THEY EXIST AT ALL ─────────────────────────────────────────────
 * 1. `pending-invite` is what makes the paywall an UPGRADE rather than a
 *    paywall: the owner types an email, hits the seat limit, pays, comes back
 *    from Stripe — and the invite they were sending goes out by itself. They
 *    never retype it. It is not cleared until the invite is actually created,
 *    so a crash mid-flow costs nothing.
 * 2. `invite-token` carries an emailed invite across an account signup. The
 *    Firebase account is created MID-wizard, so the token cannot live in a
 *    React state that a full navigation destroys.
 *
 * Every read is defensive: a corrupt or foreign value resolves to `null` and
 * is deleted, never thrown. A stash that can throw on read is a stash that can
 * white-screen a signed-in user who once had a bad write.
 */

const TTL_MS = 30 * 60 * 1000
const PENDING_INVITE_PREFIX = 'aska:pending-invite:'
const INVITE_TOKEN_KEY = 'aska:invite-token'

function store(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    // Safari private mode / a hardened browser profile. No stash, no crash:
    // the flows below all degrade to "the user retypes the email", which is
    // worse but is not a dead end.
    return null
  }
}

export interface PendingInvite {
  orgId: string
  email: string
  orgRole: 'admin' | 'member'
  createdAt: number
}

export function stashPendingInvite(v: Omit<PendingInvite, 'createdAt'>): void {
  const s = store()
  if (!s || !v.orgId) return
  try {
    s.setItem(PENDING_INVITE_PREFIX + v.orgId, JSON.stringify({ ...v, createdAt: Date.now() }))
  } catch { /* quota / disabled — the flow still works, it just re-asks */ }
}

export function readPendingInvite(orgId: string, nowMs = Date.now()): PendingInvite | null {
  const s = store()
  if (!s || !orgId) return null
  const key = PENDING_INVITE_PREFIX + orgId
  let raw: string | null = null
  try { raw = s.getItem(key) } catch { return null }
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<PendingInvite>
    const createdAt = typeof v.createdAt === 'number' ? v.createdAt : 0
    const fresh = createdAt > 0 && nowMs - createdAt < TTL_MS
    if (!fresh || typeof v.email !== 'string' || !v.email || v.orgId !== orgId) {
      s.removeItem(key)
      return null
    }
    return {
      orgId,
      email: v.email,
      orgRole: v.orgRole === 'admin' ? 'admin' : 'member',
      createdAt,
    }
  } catch {
    try { s.removeItem(key) } catch { /* ignore */ }
    return null
  }
}

export function clearPendingInvite(orgId: string): void {
  const s = store()
  if (!s || !orgId) return
  try { s.removeItem(PENDING_INVITE_PREFIX + orgId) } catch { /* ignore */ }
}

/* ── the emailed invite token ─────────────────────────────────────────── */

/**
 * The stashed invite, and whether the app has already delivered the user to
 * `/invite` for it.
 *
 * ── WHY `delivered` EXISTS: THE PIN ───────────────────────────────────────
 * `InviteWatcher` redirects a signed-in user with a stashed token to `/invite`.
 * The stash lives 30 minutes, so without a "we have already done this" mark the
 * redirect fired on EVERY navigation — and "Not now", "Browse the catalog" and
 * "Sign in as the invited person" all bounced straight back to the screen they
 * were trying to leave. That is a focus trap with no exit, which is the one
 * thing docs/ORG-FRONTEND-PLAN.md exists to prevent.
 *
 * So the redirect is a ONE-SHOT per token: the watcher marks it delivered
 * before it navigates, and after that the invite is RESUMABLE (the account-menu
 * row / profile row read the stash and route to `/invite` on purpose) but never
 * FORCED. Re-opening the emailed link — a fresh `?invite=` in the URL — re-arms
 * it, because that is an explicit act by the user.
 */
export interface InviteStash {
  token: string
  createdAt: number
  /** True once the app has auto-navigated to `/invite` for this token. */
  delivered: boolean
}

/** Stashing a token re-arms it — `?invite=` in the URL is a deliberate act.
 *  Re-stashing the SAME token keeps its delivered mark, so a re-render that
 *  writes the value back cannot silently re-arm the redirect. */
export function stashInviteToken(token: string): void {
  const s = store()
  if (!s || !token) return
  const prior = readInviteStash()
  const delivered = prior?.token === token ? prior.delivered : false
  try {
    s.setItem(INVITE_TOKEN_KEY, JSON.stringify({ token, createdAt: Date.now(), delivered }))
  } catch { /* ignore */ }
}

/** The whole stash, delivered flag included. `null` when absent/stale/corrupt. */
export function readInviteStash(nowMs = Date.now()): InviteStash | null {
  const s = store()
  if (!s) return null
  let raw: string | null = null
  try { raw = s.getItem(INVITE_TOKEN_KEY) } catch { return null }
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as { token?: unknown; createdAt?: unknown; delivered?: unknown }
    const createdAt = typeof v.createdAt === 'number' ? v.createdAt : 0
    const fresh = createdAt > 0 && nowMs - createdAt < TTL_MS
    if (!fresh || typeof v.token !== 'string' || !v.token) { s.removeItem(INVITE_TOKEN_KEY); return null }
    // A stash written by the PREVIOUS build carries no `delivered` key. Treat
    // it as undelivered: the cost is one redirect, and the alternative is an
    // invite that silently never surfaces.
    return { token: v.token, createdAt, delivered: v.delivered === true }
  } catch {
    try { s.removeItem(INVITE_TOKEN_KEY) } catch { /* ignore */ }
    return null
  }
}

export function readInviteToken(nowMs = Date.now()): string | null {
  return readInviteStash(nowMs)?.token ?? null
}

/**
 * "We have shown them this invite." Called by `InviteWatcher` immediately
 * BEFORE it navigates, so whichever exit the user then takes — a button, the
 * back button, a typed URL — actually leaves.
 */
export function markInviteDelivered(): void {
  const s = store()
  const stash = readInviteStash()
  if (!s || !stash || stash.delivered) return
  try {
    s.setItem(INVITE_TOKEN_KEY, JSON.stringify({ ...stash, delivered: true }))
  } catch { /* ignore */ }
}

export function clearInviteToken(): void {
  const s = store()
  if (!s) return
  try { s.removeItem(INVITE_TOKEN_KEY) } catch { /* ignore */ }
}

/** Called on sign-out. One person's pending invite and invite token must
 *  never be inherited by the next person on the same machine. */
export function clearOrgStashes(): void {
  const s = store()
  if (!s) return
  try {
    const doomed: string[] = []
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i)
      if (k && (k === INVITE_TOKEN_KEY || k.startsWith(PENDING_INVITE_PREFIX))) doomed.push(k)
    }
    doomed.forEach((k) => s.removeItem(k))
  } catch { /* ignore */ }
}
